import type { FocusBoundary, FocusSpeed } from "./types";

export const DEFAULT_FOCUS_BOUNDARY: FocusBoundary = {
  x: 25,
  y: 25,
  w: 50,
  h: 50,
  speed: "normal",
};

export const FOCUS_PRESETS: Record<string, { label: string; boundary: FocusBoundary }> = {
  center: {
    label: "Center",
    boundary: { x: 25, y: 25, w: 50, h: 50, speed: "normal" },
  },
  "top-left": {
    label: "Top Left",
    boundary: { x: 5, y: 5, w: 50, h: 50, speed: "normal" },
  },
  "top-right": {
    label: "Top Right",
    boundary: { x: 45, y: 5, w: 50, h: 50, speed: "normal" },
  },
  "bottom-left": {
    label: "Bottom Left",
    boundary: { x: 5, y: 45, w: 50, h: 50, speed: "normal" },
  },
  "bottom-right": {
    label: "Bottom Right",
    boundary: { x: 45, y: 45, w: 50, h: 50, speed: "normal" },
  },
};

/**
 * Maps zoom transition speed setting to milliseconds.
 */
export function focusDurationMs(speed?: FocusSpeed | null): number {
  if (speed === "slow") return 1400;
  if (speed === "fast") return 400;
  return 800;
}

/**
 * Validates and clamps focus boundary within legal 0-100% ranges.
 */
export function clampFocusBoundary(b: Partial<FocusBoundary>): FocusBoundary {
  const w = Math.min(100, Math.max(5, Number(b.w ?? DEFAULT_FOCUS_BOUNDARY.w)));
  const h = Math.min(100, Math.max(5, Number(b.h ?? DEFAULT_FOCUS_BOUNDARY.h)));
  const x = Math.min(100 - w, Math.max(0, Number(b.x ?? DEFAULT_FOCUS_BOUNDARY.x)));
  const y = Math.min(100 - h, Math.max(0, Number(b.y ?? DEFAULT_FOCUS_BOUNDARY.y)));
  const speed: FocusSpeed = b.speed === "slow" || b.speed === "fast" || b.speed === "normal" ? b.speed : "normal";

  return {
    x: Math.round(x * 100) / 100,
    y: Math.round(y * 100) / 100,
    w: Math.round(w * 100) / 100,
    h: Math.round(h * 100) / 100,
    speed,
  };
}

/**
 * The zoom that makes a focus boundary fill the screen: the largest scale at which the whole box
 * still fits, so the viewer sees exactly what was boxed and nothing is cropped out of it.
 * w and h are percentages of the screen's own width and height, so `100 / w` and `100 / h` are the
 * scales at which the box would span the screen's width and height. The smaller one is the fit.
 * A box covering the whole screen gives 1 (no zoom).
 */
export function computeFocusScale(b: FocusBoundary): number {
  const scaleW = 100 / Math.max(1, b.w);
  const scaleH = 100 / Math.max(1, b.h);
  return Math.max(1, Math.min(scaleW, scaleH));
}

export interface FocusTransform {
  scale: number;
  translateX: number;
  translateY: number;
  transform: string;
}

const NO_TRANSFORM: FocusTransform = { scale: 1, translateX: 0, translateY: 0, transform: "none" };

/**
 * Computes CSS transform parameters that frame the focus boundary: zoomed to fit it exactly (see
 * computeFocusScale) and centred on it. Translation is a percentage of the screen, applied after
 * the scale, so a point at (px, py) % of the screen lands at (s * px + tx, s * py + ty) %.
 * When unzoomed or there is no focus boundary, returns a clean reset transform.
 */
export function computeFocusTransform(b: FocusBoundary | null | undefined, zoomed = true): FocusTransform {
  if (!b || !zoomed || b.w <= 0 || b.h <= 0) return NO_TRANSFORM;

  // Rounded finely enough that the box lands on the screen's edges to a fraction of a pixel, and
  // the translation is worked out from the rounded scale so the two always agree.
  const round = (v: number) => Math.round(v * 10000) / 10000;
  const scale = round(computeFocusScale(b));

  // Put the centre of the box at the centre of the screen: 50 = s * cx + tx.
  const translateX = round(50 - scale * (b.x + b.w / 2));
  const translateY = round(50 - scale * (b.y + b.h / 2));
  if (scale === 1 && translateX === 0 && translateY === 0) return NO_TRANSFORM;

  return { scale, translateX, translateY, transform: `translate(${translateX}%, ${translateY}%) scale(${scale})` };
}

/**
 * A clip-path, in the page's own (untransformed) coordinates, that hides everything outside the
 * focus boundary. Applied to the element the focus transform is applied to, it leaves only the boxed
 * area visible once the camera has zoomed in. Unzoomed it clips nothing, so the two states animate
 * into each other.
 */
export function computeFocusClip(b: FocusBoundary | null | undefined, zoomed = true): string {
  if (!b || !zoomed || b.w <= 0 || b.h <= 0) return "inset(0% 0% 0% 0%)";
  const pct = (v: number) => `${Math.round(Math.min(100, Math.max(0, v)) * 100) / 100}%`;
  return `inset(${pct(b.y)} ${pct(100 - b.x - b.w)} ${pct(100 - b.y - b.h)} ${pct(b.x)})`;
}

/**
 * Checks if a step has an active focal crop boundary.
 */
export function isFocusedStep(b: FocusBoundary | null | undefined): boolean {
  return !!b && typeof b.x === "number" && typeof b.y === "number" && (b.w ?? 0) > 0 && (b.h ?? 0) > 0;
}

/**
 * Calculates new focus boundary after moving by target percentages.
 */
export function moveFocusBoundary(
  b: FocusBoundary,
  newX: number,
  newY: number,
): FocusBoundary {
  return clampFocusBoundary({
    ...b,
    x: newX,
    y: newY,
  });
}

export type FocusResizeCorner = "se" | "sw" | "ne" | "nw";

/**
 * Calculates new focus boundary after resizing from a specific corner.
 */
export function resizeFocusBoundary(
  b: FocusBoundary,
  corner: FocusResizeCorner,
  pX: number,
  pY: number,
): FocusBoundary {
  let { x, y, w, h } = b;
  const right = x + w;
  const bottom = y + h;

  if (corner === "se") {
    w = pX - x;
    h = pY - y;
  } else if (corner === "sw") {
    x = Math.min(pX, right - 5);
    w = right - x;
    h = pY - y;
  } else if (corner === "ne") {
    y = Math.min(pY, bottom - 5);
    h = bottom - y;
    w = pX - x;
  } else if (corner === "nw") {
    x = Math.min(pX, right - 5);
    y = Math.min(pY, bottom - 5);
    w = right - x;
    h = bottom - y;
  }

  return clampFocusBoundary({ ...b, x, y, w, h });
}

